import { Grid, Paper } from "@mui/material";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ForwardedRef, forwardRef, useRef, useState } from "react";
import { useCasterOptionFunctions } from "../../../caster/processes/hooks/useCasterOptionFunctions";
import type { CASProcessByShift } from "../../models/ProcessByShift";
import type { SelectedProcess } from "../models/SelectedProcess";
import { CommentModal } from "@/controls/ag-grid/components/CommentModal";
import { useCASTableData } from "./useCASTableData";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { getTableHeight, getMinTableHeight } from "@/utils/agGridUtils";
import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { upsertCASProcessData } from "../../repositories/ProductionRepository";

interface Props {
  data: CASProcessByShift[];
  onGridReady?: (params: GridReadyEvent) => void;
  selectedProcess: SelectedProcess | null;
  setSelectedProcess: (process: SelectedProcess | null) => void;
}

export const CASProductionTable = forwardRef<AgGridReact, Props>(
  (
    {
      data,
      onGridReady: externalGridReady,
      selectedProcess,
      setSelectedProcess,
    },
    ref: ForwardedRef<AgGridReact>,
  ) => {
    const gridAPI = useRef<GridApi | null>(null);
    const [positionToOpenModal, setPositionToOpenModal] = useState<{
      top: number;
      left: number;
    }>();

    const { showCommentModal, setShowCommentModal } =
      useCasterOptionFunctions();

    const handleShowCommentModal = (
      show: boolean,
      data: CASProcessByShift,
      position?: { top: number; left: number },
    ) => {
      if (data) {
        setSelectedProcess(data);
      }
      setPositionToOpenModal(position);
      setShowCommentModal(show);
    };

    const { columns: columnData } = useCASTableData({
      showCommentModal,
      setShowCommentModal: handleShowCommentModal,
    });
    const { columnTypes } = useGridDefinitions({});

    const handleGridReady = (params: GridReadyEvent) => {
      gridAPI.current = params.api;
      params.api.sizeColumnsToFit();

      if (externalGridReady) {
        externalGridReady(params);
      }
    };

    return (
      <Grid container>
        <Grid
          component={Paper}
          elevation={3}
          item
          md={12}
          xs={12}
          sx={{
            height: getTableHeight(data.length),
            minHeight: getMinTableHeight(),
            p: "10px",
            m: "20px 0 10px",
          }}
        >
          <AgGridReact<CASProcessByShift>
            ref={ref}
            defaultColDef={defaultColDef}
            columnTypes={columnTypes}
            onGridReady={handleGridReady}
            rowData={data}
            columnDefs={columnData}
            {...defaultGridOptions}
          />
          <CommentModal
            show={showCommentModal}
            onHide={() => {
              setShowCommentModal(false);
            }}
            position={positionToOpenModal}
            selectedElement={
              selectedProcess
                ? { ...selectedProcess, ElementId: selectedProcess.ProcessId }
                : null
            }
            setSelectedElement={setSelectedProcess}
            upsertFunction={upsertCASProcessData}
          />
        </Grid>
      </Grid>
    );
  },
);
